import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import { fetchRecruitmentSettings, saveRecruitmentSettings } from '../../../api/recruitment.api';

export default function RecruitmentSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [accepting, setAccepting] = useState(true);
  const [honeypot, setHoneypot] = useState(true);
  const [maxKb, setMaxKb] = useState(5120);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      const r = await fetchRecruitmentSettings();
      if (r.ok === false) {
        if (r.unauthorized) navigate('/admin/login', { replace: true });
        else setError(r.message);
        return;
      }
      setEmail(r.data.notify_email ?? '');
      setAccepting(r.data.accepting_applications);
      setHoneypot(r.data.honeypot_enabled);
      setMaxKb(r.data.cv_max_kilobytes);
    })();
  }, [navigate]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const r = await saveRecruitmentSettings({
      notify_email: email || null,
      accepting_applications: accepting,
      honeypot_enabled: honeypot,
      cv_max_kilobytes: maxKb,
    });
    setSaving(false);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    setSaved(true);
  };

  return (
    <AdminLayout>
      <form onSubmit={(e) => void onSave(e)} className="max-w-xl flex flex-col gap-5">
        <div>
          <h1 className="text-xl font-black text-gray-900">{t('page.recruitment_settings.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('page.recruitment_settings.subtitle')}</p>
        </div>
        {error ? <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div> : null}
        {saved ? <div className="rounded-xl bg-emerald-50 text-emerald-700 text-sm px-3 py-2">{t('recruitment.settings.saved')}</div> : null}

        <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5 space-y-4">
          <label className="block text-xs font-bold text-gray-500">
            {t('recruitment.settings.notify_email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              dir="ltr"
            />
          </label>
          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
            <input type="checkbox" checked={accepting} onChange={(e) => setAccepting(e.target.checked)} />
            {t('recruitment.settings.accepting')}
          </label>
          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
            <input type="checkbox" checked={honeypot} onChange={(e) => setHoneypot(e.target.checked)} />
            {t('recruitment.settings.honeypot')}
          </label>
          <label className="block text-xs font-bold text-gray-500">
            {t('recruitment.settings.cv_max')}
            <input
              type="number"
              min={512}
              max={20480}
              value={maxKb}
              onChange={(e) => setMaxKb(Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
        </div>

        <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold w-fit disabled:opacity-60">
          {saving ? t('recruitment.saving') : t('recruitment.save')}
        </button>
      </form>
    </AdminLayout>
  );
}
